import { create } from "zustand";
import {
  normalizeCusip,
  normalizeCusipList,
} from "../Utils/maInventoryMarketDataUtils";

const DEFAULT_PAGE_HEALTH = {
  pipelineName: "",
  pipelineDisplayName: "MD Pipeline",
  pipelineStatus: "UNKNOWN",
  modules: [],
  messageTimestamp: null,
  lastHeartbeatAt: null,
  issueCode: "waiting",
};

export const DAILY_PROCESS_STATUS = {
  UNKNOWN: "UNKNOWN",
  IN_PROGRESS: "IN_PROGRESS",
  COMPLETED: "COMPLETED",
  ERROR: "ERROR",
  FAILED: "FAILED",
};

export const DAILY_PROCESS_BLOCKING_STATUS = DAILY_PROCESS_STATUS.IN_PROGRESS;

export const DAILY_PROCESS_ADMIN_ALERT_STATUSES = new Set([
  DAILY_PROCESS_STATUS.FAILED,
]);

const buildDefaultDailyProcessStatus = () => ({
  status: "UNKNOWN",
  startTime: null,
  endTime: null,
  durationSeconds: null,
  totalProcessed: null,
  totalInserted: null,
  totalDeleted: null,
  totalErrors: null,
  message: null,
  checkedAt: null,
  isLoading: false,
  isBlocking: false,
});

const firstDefined = (...values) =>
  values.find((value) => value !== null && value !== undefined);

const getSecmasterGovState = (secmaster = {}, fallback) =>
  firstDefined(
    secmaster.govtState,
    secmaster.govState,
    secmaster.gocState,
    secmaster.state,
    fallback
  );

export const normalizeDailyProcessStatusPayload = (payload = {}) => {
  const status =
    String(payload?.status || "").trim().toUpperCase()
    || DAILY_PROCESS_STATUS.UNKNOWN;

  return {
    status,
    startTime: payload?.startTime || null,
    endTime: payload?.endTime || null,
    durationSeconds: payload?.durationSeconds ?? null,
    totalProcessed: payload?.totalProcessed ?? null,
    totalInserted: payload?.totalInserted ?? null,
    totalDeleted: payload?.totalDeleted ?? null,
    totalErrors: payload?.totalErrors ?? null,
    message: payload?.message || null,
    checkedAt: new Date().toISOString(),
    isLoading: false,
    isBlocking: status === DAILY_PROCESS_BLOCKING_STATUS,
    showAdminAlert: DAILY_PROCESS_ADMIN_ALERT_STATUSES.has(status),
  };
};

export const mapLiveMarketDataToRow = (item, existingRow = {}) => {
  const secmaster = item.secmaster || {};
  const updates = item.updates;
  const hasUpdatesPayload = updates && typeof updates === "object";
  const top = hasUpdatesPayload && Array.isArray(updates.top) ? updates.top : [];
  const depth = hasUpdatesPayload && Array.isArray(updates.depth) ? updates.depth : [];

  const bestBid = top.find(t => t.mdEntryType === "BID");
  const bestOffer = top.find(t => t.mdEntryType === "OFFER");

  const bids = depth.filter(d => d.mdEntryType === "BID");
  const offers = depth.filter(d => d.mdEntryType === "OFFER");
  
  const maxLevels = Math.max(bids.length, offers.length);
  const mappedDealers = [];
  
  for (let i = 0; i < maxLevels; i++) {
    const bid = bids[i];
    const offer = offers[i];
    
    mappedDealers.push({
      bidDealer: bid?.mdEntryOriginator,
      bidYield: bid?.customYield,
      bidQuantity: bid?.mdEntrySize,
      bidPrice: bid?.mdEntryPx,
      offerDealer: offer?.mdEntryOriginator,
      offerYield: offer?.customYield,
      offerQuantity: offer?.mdEntrySize,
      offerPrice: offer?.mdEntryPx,
    });
  }

  const nextBidDealer = hasUpdatesPayload ? bestBid?.mdEntryOriginator : existingRow.bidDealer;
  const nextBidYield = hasUpdatesPayload ? bestBid?.customYield : existingRow.bidYield;
  const nextBidQuantity = hasUpdatesPayload ? bestBid?.mdEntrySize : existingRow.bidQuantity;
  const nextBidPrice = hasUpdatesPayload ? bestBid?.mdEntryPx : existingRow.bidPrice;

  const nextOfferDealer = hasUpdatesPayload ? bestOffer?.mdEntryOriginator : existingRow.offerDealer;
  const nextOfferYield = hasUpdatesPayload ? bestOffer?.customYield : existingRow.offerYield;
  const nextOfferQuantity = hasUpdatesPayload ? bestOffer?.mdEntrySize : existingRow.offerQuantity;
  const nextOfferPrice = hasUpdatesPayload ? bestOffer?.mdEntryPx : existingRow.offerPrice;

  const nextDealers = hasUpdatesPayload ? mappedDealers : existingRow.dealers || [];

  return {
    state: getSecmasterGovState(secmaster, existingRow.state),
    moody: secmaster.moody,
    sp: secmaster.sp,
    cusip: secmaster.cusip || "",
    type: secmaster.type,
    securityCharge: firstDefined(
      secmaster.securityCharge,
      secmaster.security_charge,
      existingRow.securityCharge
    ),
    subType: secmaster.subType,
    description: secmaster.description || "",
    callable: secmaster.callable,
    taxable: secmaster.taxable,
    issuer: secmaster.issuer,
    insured: secmaster.insured,
    paymentFrequency: secmaster.paymentFrequency,
    sector: secmaster.sector,
    incomeSource: secmaster.incomeSource,
    maturity: secmaster.maturity || "",
    callStartDate: firstDefined(secmaster.callStartDate, secmaster.call_start_date),
    nextCallDate: secmaster.nextCallDate,
    altMinTax: secmaster.altMinTax,
    conventionMethod: secmaster.conventionMethod,
    underlyingIssuerRating: secmaster.underlyingIssuerRating,
    issueDate: secmaster.issueDate || "",
    coupon: secmaster.coupon,
    currency: firstDefined(secmaster.currency, secmaster.ccy),
    issueCurrency: firstDefined(
      secmaster.issueCurrency,
      secmaster.issueCcy,
      secmaster.issue_currency
    ),
    couponType: firstDefined(secmaster.couponType, secmaster.coupon_type),
    perpetual: firstDefined(secmaster.perpetual, secmaster.isPerpetual),
    zeroCoupon: firstDefined(secmaster.zeroCoupon, secmaster.isZeroCoupon, secmaster.zero_coupon),
    
    bidDealer: nextBidDealer,
    bidYield: nextBidYield,
    bidQuantity: nextBidQuantity,
    bidPrice: nextBidPrice,

    offerDealer: nextOfferDealer,
    offerYield: nextOfferYield,
    offerQuantity: nextOfferQuantity,
    offerPrice: nextOfferPrice,

    dealers: nextDealers
  };
};

// Applies one live market-data payload to a row, preserving the tick handler's
// partial-merge semantics: secmaster fields merge only when the payload carries a
// non-empty secmaster; bid/offer fields merge only when a best bid/offer is
// present; dealers replace only when depth is present. Kept field-for-field
// identical to the previous inline handler so batching cannot change what lands.
export const applyPayloadToRow = (existingRow, payload) => {
  if (!payload || typeof payload !== "object") return existingRow;

  const secmaster = payload.secmaster || {};
  const updates = payload.updates || {};
  const top = Array.isArray(updates.top) ? updates.top : [];
  const depth = Array.isArray(updates.depth) ? updates.depth : [];

  const bestBid = top.find((t) => t.mdEntryType === "BID");
  const bestOffer = top.find((t) => t.mdEntryType === "OFFER");

  let bidDealer;
  let bidYield;
  let bidQuantity;
  let bidPrice;
  let offerDealer;
  let offerYield;
  let offerQuantity;
  let offerPrice;

  if (bestBid) {
    bidDealer = bestBid.mdEntryOriginator;
    bidYield = bestBid.customYield;
    bidQuantity = bestBid.mdEntrySize;
    bidPrice = bestBid.mdEntryPx;
  }

  if (bestOffer) {
    offerDealer = bestOffer.mdEntryOriginator;
    offerYield = bestOffer.customYield;
    offerQuantity = bestOffer.mdEntrySize;
    offerPrice = bestOffer.mdEntryPx;
  }

  const bids = depth.filter((d) => d.mdEntryType === "BID");
  const offers = depth.filter((d) => d.mdEntryType === "OFFER");

  const maxLevels = Math.max(bids.length, offers.length);
  const mappedDealers = [];

  for (let i = 0; i < maxLevels; i++) {
    const bid = bids[i];
    const offer = offers[i];

    mappedDealers.push({
      bidDealer: bid?.mdEntryOriginator,
      bidYield: bid?.customYield,
      bidQuantity: bid?.mdEntrySize,
      bidPrice: bid?.mdEntryPx,
      offerDealer: offer?.mdEntryOriginator,
      offerYield: offer?.customYield,
      offerQuantity: offer?.mdEntrySize,
      offerPrice: offer?.mdEntryPx,
    });
  }

  return {
    ...existingRow,
    ...(Object.keys(secmaster).length > 0 && {
      state: getSecmasterGovState(secmaster, existingRow.state),
      moody: secmaster.moody,
      sp: secmaster.sp,
      cusip: secmaster.cusip || existingRow.cusip,
      type: secmaster.type,
      securityCharge: firstDefined(
        secmaster.securityCharge,
        secmaster.security_charge,
        existingRow.securityCharge
      ),
      subType: secmaster.subType,
      description: secmaster.description || existingRow.description,
      callable: secmaster.callable,
      taxable: secmaster.taxable,
      issuer: secmaster.issuer,
      insured: secmaster.insured,
      paymentFrequency: secmaster.paymentFrequency,
      sector: secmaster.sector,
      incomeSource: secmaster.incomeSource,
      maturity: secmaster.maturity || existingRow.maturity,
      callStartDate: firstDefined(
        secmaster.callStartDate,
        secmaster.call_start_date,
        existingRow.callStartDate
      ),
      nextCallDate: secmaster.nextCallDate,
      altMinTax: secmaster.altMinTax,
      conventionMethod: secmaster.conventionMethod,
      underlyingIssuerRating: secmaster.underlyingIssuerRating,
      issueDate: secmaster.issueDate || existingRow.issueDate,
      coupon: secmaster.coupon,
      currency: firstDefined(secmaster.currency, secmaster.ccy),
      issueCurrency: firstDefined(
        secmaster.issueCurrency,
        secmaster.issueCcy,
        secmaster.issue_currency
      ),
      couponType: firstDefined(secmaster.couponType, secmaster.coupon_type),
      perpetual: firstDefined(secmaster.perpetual, secmaster.isPerpetual),
      zeroCoupon: firstDefined(secmaster.zeroCoupon, secmaster.isZeroCoupon, secmaster.zero_coupon),
    }),
    ...(bestBid && { bidDealer, bidYield, bidQuantity, bidPrice }),
    ...(bestOffer && { offerDealer, offerYield, offerQuantity, offerPrice }),
    dealers: mappedDealers.length > 0 ? mappedDealers : existingRow.dealers,
  };
};

// cusip -> array index, so a live tick resolves its row in O(1) instead of an
// O(n) scan of every row. Rebuilt on each full row-set replacement; ticks and
// clears preserve row order, so the index stays valid between rebuilds.
const buildCusipIndex = (rows = []) => {
  const index = new Map();
  rows.forEach((row, i) => {
    const key = normalizeCusip(row?.cusip);
    if (key) index.set(key, i);
  });
  return index;
};

// Stable, normalized list of the row-set's CUSIPs. Recomputed only on a full
// row-set replacement so the value that drives live subscriptions keeps a stable
// reference across ticks (ticks patch values, never membership).
const buildCusipList = (rows = []) => normalizeCusipList(rows.map((row) => row?.cusip));

// Debug logging for the live market-data flow. Opt in via
// localStorage.setItem("MA_MD_DEBUG", "true") + reload (read once at load —
// the same switch that enables the subscription layer's per-message logs).
// Per-tick logs are hot-path; keep OFF except while debugging.
const MA_MD_STORE_DEBUG = (() => {
  try {
    return (
      typeof window !== "undefined"
      && window.localStorage?.getItem("MA_MD_DEBUG") === "true"
    );
  } catch (error) {
    return false;
  }
})();

const describeTopEntry = (entry) =>
  entry
    ? `${entry.mdEntryPx ?? "-"}x${entry.mdEntrySize ?? "-"}@${entry.mdEntryOriginator ?? "-"}`
    : "<none>";

// Live ticks are buffered and applied once per animation frame instead of once
// per message. This collapses hundreds of per-message store writes/renders into
// at most one per frame — the change that makes a 5k-row live watchlist viable.
let pendingMarketDataUpdates = new Map(); // normalized cusip -> payloads (arrival order)
let marketDataFlushScheduled = false;

// Exported so tests (and any imperative caller) can apply buffered ticks
// deterministically instead of waiting on the animation frame.
export const flushMarketDataUpdates = () => {
  marketDataFlushScheduled = false;
  if (pendingMarketDataUpdates.size === 0) return;

  // Swap the buffer out before the setState updater runs so it stays pure and any
  // tick that arrives mid-flush lands in the next batch, not this one.
  const batch = pendingMarketDataUpdates;
  pendingMarketDataUpdates = new Map();

  if (MA_MD_STORE_DEBUG) {
    const { cusipIndex } = useMAInventoryStore.getState();
    const applied = [];
    const skipped = [];
    batch.forEach((payloads, cusip) => {
      (cusipIndex?.has(cusip) ? applied : skipped).push(`${cusip}(${payloads.length})`);
    });
    console.log(
      `[MA-MD-STORE] FLUSH cusips=${batch.size} applied=${applied.length} skippedNotInWatchlist=${skipped.length}`
      + `${skipped.length ? ` skipped=${skipped.slice(0, 5).join(", ")}${skipped.length > 5 ? " …" : ""}` : ""}`
    );
  }

  useMAInventoryStore.setState((state) => {
    const data = state.maInventoryData;
    if (!data.length) return state;

    // In production the index is built by setMAInventoryData; rebuild defensively
    // if the row set was populated another way (e.g. tests) so lookups still hit.
    let cusipIndex = state.cusipIndex;
    let indexRebuilt = false;
    if (!cusipIndex || cusipIndex.size === 0) {
      cusipIndex = buildCusipIndex(data);
      indexRebuilt = true;
    }

    let nextData = null; // copy-on-write: an idle frame allocates nothing
    batch.forEach((payloads, cusip) => {
      const index = cusipIndex.get(cusip);
      if (index === undefined) return; // cusip not part of the current watchlist
      if (!nextData) nextData = data.slice();
      // Apply every buffered payload in arrival order — partial (bid-only /
      // offer-only / secmaster-only) messages each land, matching per-message.
      nextData[index] = payloads.reduce(
        (row, payload) => applyPayloadToRow(row, payload),
        nextData[index]
      );
    });

    if (!nextData) {
      return indexRebuilt ? { cusipIndex } : state;
    }

    return {
      maInventoryData: nextData,
      lastUpdatedAt: new Date().toISOString(),
      ...(indexRebuilt && { cusipIndex }),
    };
  });
};

// rAF does not fire in hidden tabs; fall back to a timeout while hidden (browsers
// throttle background timeouts to ~1s, which still keeps the buffer bounded).
const HIDDEN_TAB_FLUSH_DELAY_MS = 250;

const scheduleMarketDataFlush = () => {
  if (marketDataFlushScheduled) return;
  marketDataFlushScheduled = true;

  const canUseAnimationFrame =
    typeof window !== "undefined"
    && typeof window.requestAnimationFrame === "function"
    && (typeof document === "undefined" || document.visibilityState !== "hidden");

  if (canUseAnimationFrame) {
    window.requestAnimationFrame(flushMarketDataUpdates);
  } else {
    setTimeout(flushMarketDataUpdates, HIDDEN_TAB_FLUSH_DELAY_MS);
  }
};

const clearPendingMarketDataUpdates = () => {
  pendingMarketDataUpdates.clear();
};

// ---------------------------------------------------------------------------
// Client-side range filters (Bid/Offer Qty + Bid/Offer Px). Filtering runs
// entirely over the prefetched, live-updating rows — nothing is sent to the
// backend. Each field is a { min, max } range; an empty bound is unbounded on
// that side. The helpers below are pure so the page can filter in a memo and
// the modal can seed its draft from the same shape.
// ---------------------------------------------------------------------------
// Display/iteration order: Bid Yield, Bid Qty, Bid Px, Offer Qty, Offer Px, Offer
// Yield — bid/offer yields on the outer edges, mirroring the Watchlist grid order.
// Only the filter modal cares about this order (every other consumer iterates
// order-independently).
export const MA_INVENTORY_FILTER_FIELDS = [
  { key: "bidYield", label: "Bid Yield" },
  { key: "bidQuantity", label: "Bid Qty" },
  { key: "bidPrice", label: "Bid Px" },
  { key: "offerQuantity", label: "Offer Qty" },
  { key: "offerPrice", label: "Offer Px" },
  { key: "offerYield", label: "Offer Yield" },
];

export const buildDefaultMAInventoryFilters = () =>
  MA_INVENTORY_FILTER_FIELDS.reduce((accumulator, { key }) => {
    accumulator[key] = { min: "", max: "" };
    return accumulator;
  }, {});

// null when the value is blank (null / undefined / empty / whitespace-only) or does
// not parse to a finite number; otherwise the number.
//
// Used for BOTH filter bounds and row cell values, because "blank" must mean the
// same thing on each side: a blank bound is "unbounded", a blank cell is "no quote
// on this side" (the grid renders it as "---"). Routing cell values through this
// instead of a bare Number() matters — Number(null) and Number("") are both 0, so
// a plain Number.isFinite check would treat a quote-less row as a real 0 and match
// it against any range spanning zero. The sort path classifies the same blanks via
// isUnsortableSortValue in MAInventory.jsx.
const parseFiniteNumber = (value) => {
  if (value === null || value === undefined || String(value).trim() === "") {
    return null;
  }
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

// Yield and price filters compare on a value rounded to this many decimals rather
// than the raw one. The comparison precision matches the grid display (yields and
// prices both render at 2 decimals) and the filter modal's 2-decimal input cap, and
// absorbs floating-point noise so a bound typed in the modal reliably matches a row
// (e.g. a raw yield of 1.3512 rounds to 1.35).
// Keep in sync with the filter modal's decimal caps (MA_FILTER_*_MAX_DECIMALS).
const MA_INVENTORY_FILTER_COMPARE_FIELDS = new Set([
  "bidYield",
  "offerYield",
  "bidPrice",
  "offerPrice",
]);
const MA_INVENTORY_FILTER_COMPARE_DECIMALS = 2;

// Rounds half-away-from-zero (plain Math.round rounds half toward +Infinity, which
// would treat negative values inconsistently at the .5 boundary).
const roundToDecimals = (value, decimals) => {
  const factor = 10 ** decimals;
  const sign = value < 0 ? -1 : 1;
  return (sign * Math.round(Math.abs(value) * factor)) / factor;
};

// True when at least one range has a usable min or max bound.
export const isMAInventoryFilterActive = (filters) => {
  if (!filters) return false;
  return MA_INVENTORY_FILTER_FIELDS.some(({ key }) => {
    const range = filters[key] || {};
    return parseFiniteNumber(range.min) !== null || parseFiniteNumber(range.max) !== null;
  });
};

// Keeps rows whose value falls inside every active range. A row is dropped when
// an active range references a field the row has no finite numeric value for.
export const filterMAInventoryRows = (rows = [], filters) => {
  if (!isMAInventoryFilterActive(filters)) {
    return rows;
  }

  return rows.filter((row) =>
    MA_INVENTORY_FILTER_FIELDS.every(({ key }) => {
      const range = filters[key] || {};
      const min = parseFiniteNumber(range.min);
      const max = parseFiniteNumber(range.max);
      if (min === null && max === null) {
        return true;
      }

      // A blank or non-numeric cell means the row has no quote on this side, so
      // it cannot satisfy an active range — drop it rather than reading it as 0.
      const rawValue = parseFiniteNumber(row?.[key]);
      if (rawValue === null) {
        return false;
      }
      // Yields and prices compare at a fixed 2-decimal precision (matching the
      // grid display and the filter modal's input cap) so a Min/Max entered in the
      // modal reliably matches; every other field compares its raw value directly.
      const value = MA_INVENTORY_FILTER_COMPARE_FIELDS.has(key)
        ? roundToDecimals(rawValue, MA_INVENTORY_FILTER_COMPARE_DECIMALS)
        : rawValue;
      if (min !== null && value < min) return false;
      if (max !== null && value > max) return false;
      return true;
    })
  );
};

export const useMAInventoryStore = create((set) => ({
  maInventoryData: [],
  cusipIndex: new Map(),
  cusipList: [],
  totalElements: 0,
  loading: false,
  liveMarketDataProcessing: false,
  lastUpdatedAt: null,
  maInventoryPageHealth: DEFAULT_PAGE_HEALTH,
  dailyProcessStatus: buildDefaultDailyProcessStatus(),
  maInventoryFilters: buildDefaultMAInventoryFilters(),
  setMAInventoryData: (rows, total = 0) => {
    // A new row set invalidates any ticks buffered for the previous watchlist.
    clearPendingMarketDataUpdates();
    const list = Array.isArray(rows) ? rows : [];
    set({
      maInventoryData: list,
      cusipIndex: buildCusipIndex(list),
      cusipList: buildCusipList(list),
      totalElements: total,
    });
  },
  setLoading: (isLoading) => set({ loading: isLoading }),
  setLiveMarketDataProcessing: (isProcessing) =>
    set({ liveMarketDataProcessing: Boolean(isProcessing) }),
  setDailyProcessStatus: (statusUpdate = {}) =>
    set((state) => ({
      dailyProcessStatus: {
        ...state.dailyProcessStatus,
        ...statusUpdate,
      },
    })),
  resetDailyProcessStatus: () =>
    set({
      dailyProcessStatus: buildDefaultDailyProcessStatus(),
    }),
  setMAInventoryPageHealth: (healthUpdate = {}) =>
    set((state) => ({
      maInventoryPageHealth: {
        ...state.maInventoryPageHealth,
        ...healthUpdate,
      },
    })),
  resetMAInventoryPageHealth: () =>
    set({
      maInventoryPageHealth: DEFAULT_PAGE_HEALTH,
    }),
  setMAInventoryFilters: (filters = {}) =>
    set({
      maInventoryFilters: {
        ...buildDefaultMAInventoryFilters(),
        ...filters,
      },
    }),
  resetMAInventoryFilters: () =>
    set({
      maInventoryFilters: buildDefaultMAInventoryFilters(),
    }),
}));

if (typeof window !== "undefined") {
  const clearLiveMarketDataFields = (row) => ({
    ...row,
    bidDealer: null,
    bidYield: null,
    bidQuantity: null,
    bidPrice: null,
    offerDealer: null,
    offerYield: null,
    offerQuantity: null,
    offerPrice: null,
    dealers: [],
  });

  window.addEventListener("maInventoryMarketDataUpdate", (event) => {
    const { cusip, payload } = event.detail || {};

    // Ignore empty/null payloads safely
    if (!payload || typeof payload !== "object") return;

    const key = normalizeCusip(cusip);
    if (!key) return;

    if (MA_MD_STORE_DEBUG) {
      const top = Array.isArray(payload?.updates?.top) ? payload.updates.top : [];
      const bid = top.find((t) => t?.mdEntryType === "BID");
      const offer = top.find((t) => t?.mdEntryType === "OFFER");
      const depthCount = Array.isArray(payload?.updates?.depth) ? payload.updates.depth.length : 0;
      console.log(
        `[MA-MD-STORE] TICK cusip=${key} bid=${describeTopEntry(bid)} offer=${describeTopEntry(offer)} depth=${depthCount}`
      );
    }

    // Buffer by cusip in arrival order; the frame flush applies the whole batch.
    const queue = pendingMarketDataUpdates.get(key);
    if (queue) {
      queue.push(payload);
    } else {
      pendingMarketDataUpdates.set(key, [payload]);
    }
    scheduleMarketDataFlush();
  });

  // A rAF scheduled while visible never fires once the tab hides — flush the
  // buffer at the hide transition so ticks don't pile up for the whole
  // backgrounded stretch (subsequent hidden-tab flushes take the timeout path).
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      flushMarketDataUpdates();
    }
  });

  window.addEventListener("maInventoryPageClear", (event) => {
    const action = String(event?.detail?.action || "").trim().toLowerCase();
    if (action !== "clear") return;

    // Drop buffered ticks so a queued update can't repopulate cleared fields.
    clearPendingMarketDataUpdates();

    useMAInventoryStore.setState((state) => {
      if (!Array.isArray(state.maInventoryData) || state.maInventoryData.length === 0) {
        return state;
      }

      const cleared = state.maInventoryData.map(clearLiveMarketDataFields);
      return {
        maInventoryData: cleared,
        lastUpdatedAt: new Date().toISOString(),
      };
    });
  });

  window.addEventListener("maInventoryPageHealthUpdate", (event) => {
    const payload = event?.detail?.payload;
    if (!payload || typeof payload !== "object") return;

    const pipeline = Array.isArray(payload?.pipelines) ? payload.pipelines[0] : null;
    if (!pipeline || typeof pipeline !== "object") {
      return;
    }

    const pipelineStatus = String(pipeline?.pipelineStatus || "").trim().toUpperCase();
    const modules = Array.isArray(pipeline?.modules) ? pipeline.modules : [];

    useMAInventoryStore.setState((state) => ({
      maInventoryPageHealth: {
        ...state.maInventoryPageHealth,
        pipelineName: String(pipeline?.pipelineName || "").trim(),
        pipelineDisplayName:
          String(pipeline?.displayName || "").trim() || "MD Pipeline",
        pipelineStatus: pipelineStatus || "UNKNOWN",
        modules: modules,
        messageTimestamp: payload?.timestamp || null,
        lastHeartbeatAt: new Date().toISOString(),
        originalPayload: payload,
        issueCode:
          pipelineStatus === "OK" && modules.length === 0
            ? "ok"
            : pipelineStatus === "ERROR" || modules.length > 0
              ? "pipeline-error"
              : "pipeline-unknown",
      },
    }));
  });
}
